import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EntityLinkSelector } from '@/components/shared/EntityLinkSelector';
import type { EntityType } from '@/types/link';

interface SelectedEntity {
  id: string;
  type: EntityType;
  name: string;
}

interface IncidentCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: any, links?: SelectedEntity[]) => void;
}

export function IncidentCreateDialog({ open, onOpenChange, onSave }: IncidentCreateDialogProps) {
  const [formData, setFormData] = useState({
    title: '',
    priority: 'medium',
    impact: '',
    status: 'draft'
  });
  const [selectedLinks, setSelectedLinks] = useState<SelectedEntity[]>([]);

  const handleSubmit = () => {
    onSave(formData, selectedLinks);
    setFormData({ title: '', priority: 'medium', impact: '', status: 'draft' });
    setSelectedLinks([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Report Incident</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Incident title"
            />
          </div>
          <div>
            <Label htmlFor="priority">Priority</Label>
            <Select
              value={formData.priority}
              onValueChange={(value) => setFormData({ ...formData, priority: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="impact">Impact</Label>
            <Textarea
              id="impact"
              value={formData.impact}
              onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
              placeholder="Describe the impact"
            />
          </div>

          <EntityLinkSelector
            allowedTypes={['asset', 'system', 'risk', 'control', 'evidence', 'supplier', 'task']}
            onLinksChange={setSelectedLinks}
            initialLinks={selectedLinks}
          />
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!formData.title}>
            Report Incident
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
